import {
  SubscriberResponse,
  SubscriptionList,
  useNewsletterSubscriberSA,
  useSubscribeToNewsletterSA,
  useUnsubscribeToNewsletterSA,
} from '@va/dashboard/api-client/newsletter-preferences';
import { useAddNotification } from '@va/dashboard/util-hooks';
import { DashboardAppWorker } from '@va/util/misc';
import { useCallback, useEffect, useState } from 'react';

const initialNewsletterState = {
  standalonePromotions: undefined,
  standaloneNewsletter: undefined,
};

export const useNewsletterPreferencesSa = () => {
  const [newsletterSubscription, setNewsletterSubscription] = useState<SubscriberResponse>(initialNewsletterState);
  const { showSuccessNotification, showErrorNotification } = useAddNotification();
  const {
    data: currentSubscription,
    mutate: mutateSubscription,
    isLoading: isSubscriptionLoading,
  } = useNewsletterSubscriberSA();
  const { execute: executeSubscribe } = useSubscribeToNewsletterSA();
  const { execute: executeUnsubscribe } = useUnsubscribeToNewsletterSA();

  const isSubscribedByType = useCallback(
    (type: keyof typeof SubscriptionList) => newsletterSubscription[type],
    [newsletterSubscription],
  );

  useEffect(() => {
    if (!currentSubscription) return;
    setNewsletterSubscription(currentSubscription);
  }, [currentSubscription]);

  const onNewsletterChange = useCallback(
    async (value: keyof typeof SubscriptionList) => {
      const payload = { lists: [SubscriptionList[value]] };

      try {
        if (isSubscribedByType(value)) {
          await executeUnsubscribe(payload);
          sendNewsletterEvent(false);
        } else {
          await executeSubscribe(payload);
          sendNewsletterEvent(true);
        }
        showSuccessNotification();
      } catch (err) {
        showErrorNotification();
      } finally {
        mutateSubscription();
      }
    },
    [
      executeSubscribe,
      executeUnsubscribe,
      isSubscribedByType,
      mutateSubscription,
      showErrorNotification,
      showSuccessNotification,
    ],
  );

  return { onNewsletterChange, isSubscribedByType, isSubscriptionLoading };
};

function sendNewsletterEvent(value: boolean) {
  DashboardAppWorker.trackEvent({
    action: 'onchange',
    category: 'toggle',
    label: 'Newsletter',
    value: value.toString(),
  });
}
